// 地圖 / 平面圖（plan/vms-web-v0.md §5.2）：兩個模式同一頁。
//   地圖：Google / 百度（依地區自動、可切換），場域健康、戶外設備擺放、平面圖疊圖（GeoMapView）
//   平面圖：樓層圖直接看、擺設備、事件閃爍、點攝影機看即時（FloorplanView）
// 平面圖、擺放、座標存在雲端（platform vms schema），依家戶 / 組織授權共用。
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDevices } from '../../lib/useDevices';
import { fullName } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { useTzResolver } from '../../lib/useTz';
import { parsePins, pinsByDevice, type MapEventPin } from './mapEvents';
import { EventThumb } from '../../components/EventThumb';
import { useRecentEvents } from '../../lib/useRecentEvents';
import { FloorplanView } from './FloorplanView';
import { GeoMapView } from './GeoMapView';
import { useFloorplanOwners, useFloorplans } from './useMapData';
import { eventName } from '../../lib/eventName';
import { playbackHref } from '../../lib/links';
import { classify } from '../events/EventsPage';

/** 事件發生後多久內圖示閃爍。 */
export const ALERT_WINDOW_MS = 120_000;
const MODE_KEY = 'kinio.vms.map.mode';

export function MapPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const { tree } = useDevices();
  const tzOf = useTzResolver();
  const byKey = useMemo(() => new Map(tree.flatMap((d) => [d, ...d.children]).map((d) => [d.key, d])), [tree]);
  const recent = useRecentEvents(50, 5_000);
  // MAP-03：事件頁帶過來的搜尋結果（有的話地圖只打這些點，可清除回到即時）
  const [pins, setPins] = useState<MapEventPin[]>(() => parsePins(location.state));
  const [focus, setFocus] = useState<MapEventPin | null>(null);
  const [listOpen, setListOpen] = useState(() => parsePins(location.state).length > 0);
  const owners = useFloorplanOwners();
  const { list: plans, writable } = useFloorplans(owners);
  const [mode, setModeState] = useState<'map' | 'plan'>(() => {
    try {
      return localStorage.getItem(MODE_KEY) === 'map' ? 'map' : 'plan';
    } catch {
      return 'plan';
    }
  });
  const [curId, setCurId] = useState<string | null>(null);
  const setMode = (m: 'map' | 'plan') => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* 無痕模式 */
    }
  };

  const now = Date.now();
  const alerting = useMemo(() => {
    const s = new Map<string, number>();
    if (pins.length) {
      for (const [k, v] of pinsByDevice(pins)) s.set(k, v.ts);
    } else {
      for (const r of recent.data ?? []) {
        if (now - r.ts > ALERT_WINDOW_MS) break;
        if (r.ev.type.startsWith('thing.') || r.ev.type.startsWith('ota.')) continue;
        if (!s.has(r.deviceKey)) s.set(r.deviceKey, r.ts);
        if (!s.has(r.parentKey)) s.set(r.parentKey, r.ts);
      }
    }
    if (focus) s.set(focus.deviceKey, focus.ts);
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recent.data, Math.floor(now / 5000), pins, focus]);
  // 清單：搜尋結果，或最近的事件（不含上線 / 韌體這類系統事件）
  const list: MapEventPin[] = useMemo(
    () =>
      pins.length
        ? pins
        : (recent.data ?? [])
            .filter((r) => !r.ev.type.startsWith('thing.') && !r.ev.type.startsWith('ota.'))
            .slice(0, 50)
            .map((r) => ({
              deviceKey: r.deviceKey,
              ts: r.ts,
              eventId: r.ev.id,
              homeId: r.deviceKey.split('/')[0],
              type: r.ev.type,
              level: (r.ev as { level?: string }).level ?? classify(r.ev.type, r.ev.event_class as string | undefined),
            })),
    [pins, recent.data],
  );
  // 「只看警報」（VMS UX 評審 2026-10-13 P2-5、SPEC MAP-03）：最近事件 / 搜尋結果都能篩
  const [alarmsOnly, setAlarmsOnly] = useState(false);
  const shownList = alarmsOnly ? list.filter((p) => (p.level ?? classify(p.type)) === 'alarm') : list;
  const devName = (key: string) => {
    const d = byKey.get(key);
    return d ? fullName(d, tree) : key.split('/').slice(1).join('/');
  };

  return (
    <section className="map-page">
      <div className="toolbar map-head">
        <h1 className="page-title" style={{ margin: 0 }}>
          {t('nav.map')}
        </h1>
        <div className="seg" role="tablist">
          <button role="tab" aria-pressed={mode === 'map'} onClick={() => setMode('map')}>
            {t('map.mode_map')}
          </button>
          <button role="tab" aria-pressed={mode === 'plan'} onClick={() => setMode('plan')}>
            {t('map.mode_plan')}
          </button>
        </div>
        <span className="spacer" />
        <button className="btn" aria-expanded={listOpen} onClick={() => setListOpen((o) => !o)} data-testid="map-events-toggle">
          {pins.length ? t('map.events_results', { n: pins.length }) : t('map.events_recent', { n: list.length })}
        </button>
        {pins.length > 0 && (
          <button
            className="btn"
            onClick={() => {
              setPins([]);
              setFocus(null);
            }}
          >
            {t('map.events_clear')}
          </button>
        )}
      </div>
      {listOpen && (
        <div className="card map-events" role="region" aria-label={t('map.events_title')}>
          <label className="check small">
            <input type="checkbox" checked={alarmsOnly} onChange={(e) => setAlarmsOnly(e.target.checked)} data-testid="map-alarms-only" />
            {t('map.alarms_only')}
          </label>
          {shownList.length === 0 ? (
            <p className="muted small">{t('map.events_none')}</p>
          ) : (
            <ul className="map-events-list">
              {shownList.map((p) => (
                <li key={p.eventId + p.deviceKey} className={focus?.eventId === p.eventId ? 'sel' : ''} data-map-event={p.eventId}>
                  <button className="link" onClick={() => setFocus(p)}>
                    <span className="muted small">{formatDateTime(p.ts / 1000, tzOf(p.homeId), i18n.language)}</span> {devName(p.deviceKey)}
                    <span className="muted small"> · {eventName(t, p.type)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {focus && (
            <div className="map-event-card" data-testid="map-event-card">
              {byKey.get(focus.deviceKey)?.isCamera && (
                <EventThumb
                  api={byKey.get(focus.deviceKey)!.apiEndpoint}
                  homeId={focus.homeId}
                  device={focus.deviceKey.split('/').slice(1).join('/')}
                  atMs={focus.ts}
                  alt={t('events.thumb_alt')}
                  className="map-thumb"
                />
              )}
              <strong>{devName(focus.deviceKey)}</strong>
              <span className="muted small">
                {eventName(t, focus.type)} · {formatDateTime(focus.ts / 1000, tzOf(focus.homeId), i18n.language)}
              </span>
              <span className="spacer" />
              {byKey.get(focus.deviceKey)?.isCamera && (
                <button className="btn btn-sm" onClick={() => nav(playbackHref(focus.deviceKey, focus.ts / 1000, { event: focus.eventId }))}>
                  ⏪ {t('map.events_playback')}
                </button>
              )}
              <Link className="btn btn-sm" to={`/events/${encodeURIComponent(focus.homeId)}/${encodeURIComponent(focus.eventId)}`}>
                {t('evd.open')}
              </Link>
            </div>
          )}
          <p className="muted small">{t('map.events_help')}</p>
        </div>
      )}
      {mode === 'map' ? (
        <GeoMapView plans={plans} alerting={alerting} focusKey={focus?.deviceKey} onOpenPlan={(id) => (setCurId(id), setMode('plan'))} onUsePlan={() => setMode('plan')} />
      ) : (
        <FloorplanView plans={plans} writable={writable} alerting={alerting} focusKey={focus?.deviceKey} curId={curId} setCurId={setCurId} />
      )}
    </section>
  );
}
